import { MentorshipDetails, useMenteeCalendar } from "@/lib/api/mentorships";
import { CalendarHeader } from "./overview/calendar-header";
import { TeenCalendar } from "./overview/teen-calendar";
import { Skeleton } from "../ui/skeleton";
import { useState } from "react";
import { Note } from "@/lib/api/notes";
import { NoteRenderer } from "./notes/note-renderer";
import { UpcomingNotesWithSession } from "./notes/upcoming-notes-with-sessions";
import { SessionTimelineItem } from "../session/session-timeline-item";
import { Session } from "@/lib/api/sessions";
import { CancelSessionConfirmationModal } from "../session/cancel-session-confirmation-modal";
import { useAbility } from "@/lib/casl/ability-context";

export function Calendar({
  mentorship,
  notes,
  sessions,
  adminMode,
}: {
  mentorship: MentorshipDetails;
  notes: Note[];
  sessions: Session[];
  adminMode: boolean;
}) {
  const ability = useAbility();
  const canUpdate = ability.can("update", "documentation");
  const [month, setMonth] = useState(
    parseInt(new Date().toLocaleDateString("en-US", { month: "numeric" })),
  );
  const [year, setYear] = useState(
    parseInt(new Date().toLocaleDateString("en-US", { year: "numeric" })),
  );
  const [selectedSessionToDelete, setSelectedSessionToDelete] = useState<
    string | null
  >(null);

  const {
    data: events,
    isLoading,
    isError,
  } = useMenteeCalendar(mentorship.id, month, year);

  const upcomingNotes = notes
    ?.filter((note) => {
      if (!note?.reminder) return false;

      const reminderDate = new Date(note.reminder);
      const currentDate = new Date();
      return reminderDate > currentDate;
    })
    .toSorted((a, b) => {
      if (!a?.reminder || !b?.reminder) return 0;

      return Date.parse(a.reminder) - Date.parse(b.reminder);
    });

  const upcomingSessions = sessions?.filter((session) => {
    const sessionDate = new Date(session.scheduled_time);
    const currentDate = new Date();
    return sessionDate > currentDate;
  });

  console.log("admin", adminMode);

  return (
    <div className="pb-20">
      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : isError || !events ? (
        <div className="flex min-h-96 items-center justify-center">
          <div className="text-red-500">Error fetching calendar events</div>
        </div>
      ) : (
        <>
          <UpcomingNotesWithSession
            menteeId={mentorship.mentee.id}
            mentorId={mentorship.mentor.id}
            notes={upcomingNotes || []}
            sessions={upcomingSessions || []}
            readonlyMode={adminMode}
            emptyMessage="No upcoming documentation or sessions"
            renderNote={(note) => {
              return (
                <NoteRenderer
                  key={note.id}
                  note={note}
                  pinnedNotes={false}
                  readonlyMode={adminMode || !canUpdate}
                  adminMode={adminMode}
                  menteeId={mentorship?.mentee?.id}
                  mentorId={mentorship?.mentor?.id}
                />
              );
            }}
            renderSession={(session) => {
              return (
                <SessionTimelineItem
                  key={session.id}
                  heading={session.title}
                  session={session}
                  readonlyMode={adminMode}
                  onDelete={() => {
                    setSelectedSessionToDelete(session.id);
                  }}
                />
              );
            }}
          />
          <CalendarHeader
            month={month}
            year={year}
            setMonth={setMonth}
            setYear={setYear}
          />
          <TeenCalendar events={events} year={year} month={month} />
        </>
      )}
      {!!selectedSessionToDelete && (
        <CancelSessionConfirmationModal
          sessionId={selectedSessionToDelete!}
          open={!!selectedSessionToDelete}
          menteeId={mentorship.mentee.id}
          onClose={() => {
            setSelectedSessionToDelete(null);
          }}
        />
      )}
    </div>
  );
}
